import React from "react";
import { FormikProps, getIn } from "formik";

/**
 * Creates a smart blur handler that only marks a field as "touched"
 * when it has a value. Empty fields are un-touched on blur so that
 * "required" validation errors only surface after a submit attempt
 * (Formik touches all fields on failed submit).
 *
 * Format errors still show immediately: the user types an invalid
 * value → onChange validation populates errors → blur sets touched → red.
 */
export const createFieldBlurHandler =
  <T>(formik: FormikProps<T>) =>
  (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const fieldName = e.target.name;
    if (!fieldName) return;

    const value = getIn(formik.values, fieldName);

    if (value !== undefined && value !== null && value !== "") {
      formik.handleBlur(e);
    } else {
      formik.setFieldTouched(fieldName, false);
    }
  };

// ---------------------------------------------------------------------------
// Masked-input support (react-input-mask)
// ---------------------------------------------------------------------------

/** Default format characters used by react-input-mask. */
const DEFAULT_FORMAT_CHARS: Record<string, string> = {
  "9": "[0-9]",
  a: "[A-Za-z]",
  "*": "[A-Za-z0-9]",
};

/**
 * Computes what a masked input looks like when no user input has been entered.
 * E.g. mask="(999) 999 9999" with maskChar="_" → "(___) ___ ____"
 *
 * Handles the backslash escape convention used by react-input-mask
 * (e.g. "+\\9\\96" means literal 9, 9, 6).
 */
export function getEmptyMaskValue(
  mask: string,
  maskChar: string = "_",
  formatChars: Record<string, string> = DEFAULT_FORMAT_CHARS,
): string {
  let result = "";
  let i = 0;
  while (i < mask.length) {
    if (mask[i] === "\\" && i + 1 < mask.length) {
      // Escaped character — always a literal
      result += mask[i + 1];
      i += 2;
    } else if (formatChars[mask[i]]) {
      // Fillable position — placeholder
      result += maskChar;
      i++;
    } else {
      // Literal character
      result += mask[i];
      i++;
    }
  }
  return result;
}

/**
 * Creates a blur handler for masked inputs (react-input-mask).
 *
 * Works like `createFieldBlurHandler` but understands that an "empty"
 * masked field still has a non-empty string value (the mask placeholder
 * characters, e.g. "(___) ___ ____" for a phone mask).
 */
export const createMaskedFieldBlurHandler =
  <T>(
    formik: FormikProps<T>,
    mask: string,
    maskChar: string = "_",
    formatChars: Record<string, string> = DEFAULT_FORMAT_CHARS,
  ) => {
    const emptyValue = getEmptyMaskValue(mask, maskChar, formatChars);

    return (
      e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    ) => {
      const fieldName = e.target.name;
      if (!fieldName) return;

      const value = getIn(formik.values, fieldName);

      if (
        value === undefined ||
        value === null ||
        value === "" ||
        value === emptyValue
      ) {
        formik.setFieldTouched(fieldName, false);
      } else {
        formik.handleBlur(e);
      }
    };
  };
